import moment from 'moment'

/**
 * A service that identifies time entries where there is a gap between the previous
 * time entry on the same day, and adds metadata to the time entry.
 */
export class TimeGapDecorator {
    /**
     * Return whether there is a gap between the start of the given time entry
     * and the entry that directly precedes it.
     * @param {any} timeEntry The time entry to be checked.
     * @param {any} otherEntries Other entries to compare to the time entry.
     * @param {any} threshold The threshold in hours between time entries used
     * to determine whether there is a gap. If the gap is below the threshold,
     * no gap will be identified'
     * @param {String} unit The moment.js-styled unit of time used for comparison.
     * @returns {boolean} whether there is a gap between the given time entry
     * and the time entry directly preceding it on the same day.
     */
    isGapBetweenTimeEntries(timeEntry, otherEntries, threshold, unit) {
        unit = unit || "hours";
        const directlyPrecedingTimeEntry = this.getDirectlyPrecedingTimeEntry(timeEntry, otherEntries);
        if (!directlyPrecedingTimeEntry) {
            return false;
        }
        return moment.duration(timeEntry.StartTime)
            .subtract(moment.duration(directlyPrecedingTimeEntry.EndTime))
            .as(unit) > threshold;
    }

    /**
     * Set the time gap decoration details for the given time entry that has a gap set.
     * @param {any} timeEntry
     * @param {any} otherEntries
     */
    decorateTimeEntryWithGap(timeEntry, otherEntries) {
        const directlyPrecedingTimeEntry = this.getDirectlyPrecedingTimeEntry(timeEntry, otherEntries);
        if (timeEntry.IsBreak || !directlyPrecedingTimeEntry) {
            return;
        }
        const gapDuration = moment.duration(timeEntry.StartTime)
            .subtract(moment.duration(directlyPrecedingTimeEntry.EndTime));

        timeEntry.gapDetails = {
            gapDuration: gapDuration.asHours(),
            precedingTimeEntry: directlyPrecedingTimeEntry,
            rowClass: "table-warning"
        };
    }

    /**
     * Decorate the given time entry with the appropriate show data.
     * @param {any} timeEntry
     */
    decorateShow(timeEntry) {
        if (timeEntry.IsBreak) {
            // Hide the program and show if the time entry is a meal break.
            timeEntry.ProgramName = "";
            timeEntry.LocationName = "";
        }
    }

    getDirectlyPrecedingTimeEntry(timeEntry, otherEntries) {
        const date = moment(timeEntry.Date);
        const startTime = moment.duration(timeEntry.StartTime);
        return otherEntries.reduce(function (precedingEntry, otherEntry) {
            // Take the entry starting before the base entry that ends closest to the base start time.
            const otherStartTime = moment.duration(otherEntry.StartTime);
            const otherEndTime = moment.duration(otherEntry.EndTime);

            // Ignore other entries are on different dates or start after the time entry.
            if (!date.isSame(otherEntry.Date, 'day') || otherStartTime > startTime) {
                return precedingEntry;
            }

            // First valid entry in the loop, so use the entry as the initial value.
            if (!precedingEntry) {
                return otherEntry;
            }

            const precedingTime = moment.duration(precedingEntry.EndTime);
            if (otherStartTime < startTime && otherEndTime > precedingTime) {
                // Prefer the later time entry that is still before the given time entry.
                return otherEntry;
            }
            return precedingEntry;
        }, null);
    }
}
